import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5i0ddbgekujqjrtsrn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-3xl overflow-hidden shadow-xl">
          {/* Left Column - Contact Info */}
          <div className="lg:col-span-2 bg-[#8B9B7E] text-white p-12">
            <h2 className="font-serif text-4xl mb-8">{t('contact.title')}</h2>

            <div className="space-y-8">
              {/* Phone */}
              <div className="flex items-start space-x-4">
                <i className="ri-phone-line text-2xl flex-shrink-0 mt-1"></i>
                <div>
                  <p className="font-semibold mb-1">{t('contact.info.phone')}</p>
                  <a href="tel:+34643939427" className="hover:underline cursor-pointer">
                    +34 643 939 427
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4">
                <i className="ri-mail-line text-2xl flex-shrink-0 mt-1"></i>
                <div>
                  <p className="font-semibold mb-1">{t('contact.info.email')}</p>
                  <a href="mailto:art.throng@gmail.com" className="hover:underline cursor-pointer">
                    art.throng@gmail.com
                  </a>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start space-x-4">
                <i className="ri-map-pin-line text-2xl flex-shrink-0 mt-1"></i>
                <div>
                  <p className="font-semibold mb-1">{t('contact.info.location')}</p>
                  <p>{t('contact.info.locationValue')}</p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-12">
              <p className="font-semibold mb-4">{t('footer.followUs')}</p>
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.instagram.com/art.throng/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#8B9B7E] transition-all cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a
                  href="https://www.facebook.com/ArtThrong"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#8B9B7E] transition-all cursor-pointer"
                >
                  <i className="ri-facebook-circle-line text-xl"></i>
                </a>
                <a
                  href="#"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#8B9B7E] transition-all cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-3 bg-white p-12">
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.name')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('contact.form.namePlaceholder')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('contact.form.emailPlaceholder')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.form.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={t('booking.form.phonePlaceholder')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.subject')}
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700 cursor-pointer"
                  required
                >
                  <option value="">{t('contact.form.subjectPlaceholder')}</option>
                  <option value="interior-design">{t('booking.services.interior')}</option>
                  <option value="cleaning">{t('booking.services.cleaning')}</option>
                  <option value="maintenance">{t('booking.services.maintenance')}</option>
                  <option value="renovation">{t('booking.services.renovation')}</option>
                  <option value="packages">{t('packages.title')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.message')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={t('contact.form.messagePlaceholder')}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700 resize-none"
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#8B9B7E] text-white py-4 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
              </button>

              {submitStatus === 'success' && (
                <p className="text-green-600 text-center text-sm">
                  {t('contact.form.success')}
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-center text-sm">
                  {t('contact.form.error')}
                </p>
              )}
            </form>

            {/* Map */}
            <div className="mt-8 h-80 rounded-2xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30773.89!2d73.827!3d15.491!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbfc06f4b64ed4d%3A0x8f2c2c0e5e5e5e5e!2sPanjim%2C%20Goa!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Art Throng Home Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
